// Pure display logic for the stored-WiFi-credentials read-back. Maps a
// `StoredCredentialReadout` from ble-core (plus whether the tester has pressed
// "Read stored credentials" yet) to one of four display states. No React, no
// BLE — testable in isolation.
//
// The firmware-bug warning (a characteristic that advertised `read` but failed
// the read) is handled outside this helper; here, a failed read collapses to
// the same `'unavailable'` state as a write-only characteristic, because the
// tester sees the same "not available" message either way.

import type { StoredCredentialReadout } from '@soluu/ble-core';

export type CredentialDisplay =
  /** Tester has not pressed the read button yet. */
  | { readonly state: 'not-read-yet' }
  /** Not readable on this firmware (write-only, or the read failed). */
  | { readonly state: 'unavailable' }
  /** Read succeeded but the device had no value stored. */
  | { readonly state: 'empty' }
  /** Read succeeded with a value. */
  | { readonly state: 'value'; readonly value: string };

export interface CredentialDisplayInput {
  readonly readout: StoredCredentialReadout;
  readonly hasRead: boolean;
}

export function deriveCredentialDisplay(input: CredentialDisplayInput): CredentialDisplay {
  if (!input.hasRead) {
    return { state: 'not-read-yet' };
  }
  const { readable, readFailed, value } = input.readout;
  if (!readable || readFailed || value === null) {
    return { state: 'unavailable' };
  }
  if (value === '') {
    return { state: 'empty' };
  }
  return { state: 'value', value };
}
